import { computed, inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Component as ServiceAvailabilityComponent } from '../../../../../models/order/api-models/order-sq-response.model';
import { PRODUCT_TYPES, ServiceType } from '../../../../../models/order/order.model';
import { OrderApiResponseStateService } from './order-api-response-state.service';
import { VideoPackageTypeEnum } from '../../../../../enums/video-package-type.enum';

@Injectable({ providedIn: 'root' })
export class SQResponseStateService {
    private orderApiResponseStateService = inject(OrderApiResponseStateService);
    #sanitizer = inject(DomSanitizer);

    SQResponse = this.orderApiResponseStateService.serviceAvailabilityData;

    productResponse = computed(() => this.SQResponse()?.Response?.ProductResponse ?? []);

    components = computed(() => this.SQResponse()?.Components?.Component ?? []);

    hasSQResponse = computed(() => !!this.SQResponse());

    hasInternetProduct = computed(() => this.hasProduct(PRODUCT_TYPES.INTERNET));

    hasVideoProduct = computed(() => {
        return this.productResponse()
            .filter(x => ((x.Code ?? '').trim() === '0000' || (x.Code ?? '').trim() === '2009'))
            .some(item => {
                return (item.Type?.toLocaleLowerCase() === VideoPackageTypeEnum.DTV.toLocaleLowerCase() || item.Type?.toLocaleLowerCase() === VideoPackageTypeEnum.ATV.toLocaleLowerCase() || item.Type?.toLocaleLowerCase() === VideoPackageTypeEnum.GENRE.toLocaleLowerCase());
            });
    });

    hasWirelessProduct = computed(() => this.hasProduct(PRODUCT_TYPES.WIRELESS));

    hasVoiceProduct = computed(() => this.hasProduct(PRODUCT_TYPES.VOICE));

    hasProtectionProduct = computed(() => this.hasProduct(PRODUCT_TYPES.PROTECTION));

    hasProduct(productType: ServiceType): boolean {
        return this.productResponse().filter(x => x.Code && (x.Code ?? '').trim() === '0000').some(item => {
            if (productType === PRODUCT_TYPES.VOICE) {
                return (item.Type?.toLocaleLowerCase() === productType.toLocaleLowerCase() || item.Type?.toLocaleLowerCase() === PRODUCT_TYPES.VOIP.toLocaleLowerCase());
            }
            return item.Type?.toLocaleLowerCase() === productType.toLocaleLowerCase();
        });
    }

    availableProviders(productType: ServiceType): { provider: string, count: number }[] {
        const data = this.getPackages(productType);
        const map = new Map<string, number>();
        for (const item of data) {
            if (item.Provider) {
                const key = item.Provider;
                map.set(key, (map.get(key) ?? 0) + 1);
            }
        }
        return Array.from(map.entries()).map(([provider, count]) => ({ provider, count }));
    }

    packageGroups(productType: ServiceType): { provider: string, packages: ServiceAvailabilityComponent[] }[] {
        const packages = this.getPackages(productType);
        const providers = new Set(packages.map(item => item.Provider).filter(Boolean));
        return Array.from(providers).map(provider => ({
            provider,
            packages: packages.filter(item => item.Provider === provider)
        }));
    }

    videoPackageGroups(): { provider: string, packages: ServiceAvailabilityComponent[] }[] {
        const packages = this.getPackages(PRODUCT_TYPES.VIDEO);
        const providers = new Set(packages.map(item => item.CategoryType).filter(Boolean));
        return Array.from(providers).map(provider => ({
            provider: provider === VideoPackageTypeEnum.ATV ? 'Stream' : 'Satellite',
            packages: packages.filter(item => item.CategoryType === provider)
        }));
    }

    getPackages(productType: ServiceType, provider?: string | null): ServiceAvailabilityComponent[] {
        return this.components()
            .filter(item => item.Category?.toLocaleLowerCase() === productType.toLocaleLowerCase())
            .filter(item => !provider || item.Provider === provider)
            .map(item => {
                if (item.BroadbandLabelUrl && !item.BroadbandLabelUrlDown) {
                    item.BroadbandLabelSanitizedUrl = this.#sanitizer.bypassSecurityTrustResourceUrl(item.BroadbandLabelUrl);
                }
                return item;
            })
            .sort((a, b) => (Number(a.SequenceNumber) || 0) - (Number(b.SequenceNumber) || 0));
    }

    getErrorResponse(productType: string) {
        const response = this.SQResponse();
        if (response) {
            const errorResponse = response.Response?.ProductResponse?.find(x => x.Type?.toLocaleLowerCase() === productType.toLocaleLowerCase() && String(x?.Code ?? '').trim() !== '0000');
            if (errorResponse) {
                return { code: errorResponse.Code, message: errorResponse.Description ?? 'Unable to fetch service availability results, please try later.' };
            }
        }
        return null;
    }
}
